import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from "recharts";
import { BarChart3, Target } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import ChartTooltipFlua from "@/components/ChartTooltipFlua";
import { fmtBRL, fmtInt } from "@/lib/format";

/**
 * Comparativo gráfico Meta × Realizado por vendedor, em duas dimensões:
 *  - Financeiro (R$)
 *  - Clientes (qtd)
 * Cor da barra "Realizado" muda dinamicamente:
 *   verde = bateu (>=100%), âmbar = 50–99%, vermelho = <50%.
 * Um card compacto por gráfico com tooltip FLUA + legenda.
 */
export default function ComparativoMetasChart({ metrics = [], metasMap = {}, metasClientesMap = {} }) {
  const dataFin = useMemo(
    () => metrics.map((v) => ({
      nome: v.nome,
      meta: Number(metasMap[v.nome] || 0),
      realizado: Number(v.receita || 0),
    })),
    [metrics, metasMap]
  );
  const dataCli = useMemo(
    () => metrics.map((v) => ({
      nome: v.nome,
      meta: Number(metasClientesMap[v.nome] || 0),
      realizado: Number(v.vendas || 0),
    })),
    [metrics, metasClientesMap]
  );

  const toneOf = (meta, real) => {
    if (!meta) return "hsl(var(--muted-foreground))";
    const p = (real / meta) * 100;
    if (p >= 100) return "hsl(158 64% 52%)";  // emerald
    if (p >= 50) return "hsl(35 92% 58%)";    // amber
    return "hsl(0 74% 58%)";                  // red
  };

  const commonAxis = {
    stroke: "hsl(var(--muted-foreground))",
    tick: { fill: "hsl(var(--muted-foreground))", fontSize: 11 },
    tickLine: false,
    axisLine: { stroke: "hsl(var(--border))" },
  };

  if (metrics.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-5 card-flua text-sm text-muted-foreground" data-testid="comparativo-metas-empty">
        Nenhum vendedor cadastrado ainda — cadastre o time pra comparar performance.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-5 card-flua" data-testid="comparativo-metas-chart">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-primary" strokeWidth={1.5} />
          <h3 className="font-heading font-semibold text-lg">Comparativo Meta × Realizado</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-[10px] gap-1">
            <span className="w-2 h-2 rounded-sm bg-primary/60" /> Meta
          </Badge>
          <Badge variant="outline" className="text-[10px] gap-1">
            <span className="w-2 h-2 rounded-sm" style={{ background: "hsl(158 64% 52%)" }} /> ≥100%
          </Badge>
          <Badge variant="outline" className="text-[10px] gap-1">
            <span className="w-2 h-2 rounded-sm" style={{ background: "hsl(35 92% 58%)" }} /> 50–99%
          </Badge>
          <Badge variant="outline" className="text-[10px] gap-1">
            <span className="w-2 h-2 rounded-sm" style={{ background: "hsl(0 74% 58%)" }} /> &lt;50%
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Financeiro */}
        <div data-testid="chart-metas-financeiro">
          <div className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-widest text-emerald-500 font-semibold">
            <Target className="w-3 h-3" strokeWidth={2} /> Financeiro (R$)
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dataFin} margin={{ top: 8, right: 8, bottom: 4, left: -8 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="nome" {...commonAxis} interval={0} />
              <YAxis {...commonAxis} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
              <Tooltip cursor={{ fill: "hsl(var(--primary) / 0.06)" }} content={<ChartTooltipFlua valueFormatter={(v) => fmtBRL(v)} />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="meta" name="Meta" fill="hsl(var(--primary))" fillOpacity={0.45} radius={[6, 6, 0, 0]} />
              <Bar dataKey="realizado" name="Realizado" radius={[6, 6, 0, 0]}>
                {dataFin.map((d, i) => (
                  <Cell key={i} fill={toneOf(d.meta, d.realizado)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Clientes */}
        <div data-testid="chart-metas-clientes">
          <div className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-widest text-primary font-semibold">
            <Target className="w-3 h-3" strokeWidth={2} /> Clientes (qtd)
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dataCli} margin={{ top: 8, right: 8, bottom: 4, left: -8 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="nome" {...commonAxis} interval={0} />
              <YAxis {...commonAxis} allowDecimals={false} />
              <Tooltip cursor={{ fill: "hsl(var(--primary) / 0.06)" }} content={<ChartTooltipFlua valueFormatter={(v) => fmtInt(v)} />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="meta" name="Meta" fill="hsl(var(--primary))" fillOpacity={0.45} radius={[6, 6, 0, 0]} />
              <Bar dataKey="realizado" name="Realizado" radius={[6, 6, 0, 0]}>
                {dataCli.map((d, i) => (
                  <Cell key={i} fill={toneOf(d.meta, d.realizado)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
